// The ONE place an OGS ONNX decoder session is born.
//
// Three things every decoder build needs, in one place so no call site can skip
// one (a bundle's shipped fp16 decoders are unreachable without all three):
//   1. the ORT env assert (wasmPaths / numThreads / proxy=false),
//   2. the fp16-preferred → fp32-fallback resolve (decoderResolve.ts, untouched),
//   3. the warm per-precision session cache behind the Avatar node's dropdown.
//
// ensureOgsOrtEnv runs INSIDE the gated create callback on purpose: `ort.env` is
// a shared singleton and the hair runner sets `proxy = true` for its worker
// offload. ORT captures proxy per-session AT CREATE TIME, so re-asserting it
// immediately before each create — while holding the gate, so no hair create can
// interleave — is strictly safer than the runner's single init()-time assert.

import { DecoderSession, type DecoderSessionOptions } from "./DecoderSession.ts";
import { withOrtGate } from "../../lib/ortGate.js";
import { resolveDecoderSession } from "../inference/decoderResolve.ts";

type OrtEnvLike = {
  env: { wasm: { wasmPaths?: string; numThreads?: number; proxy?: boolean } };
};

/** Assert the OGS ORT env. Idempotent, cheap; call before every session create. */
export function ensureOgsOrtEnv(ort: OrtEnvLike): void {
  // Runtime WASM/JSEP files are served from the host's /onnxruntime/ route
  // (vite/vite_plugin_onnxruntime_static.mjs).
  ort.env.wasm.wasmPaths = "/onnxruntime/";
  // Single-threaded: the threaded build needs crossOriginIsolated (COOP/COEP),
  // which this app's dev/prod servers don't enable.
  ort.env.wasm.numThreads = 1;
  // Main thread, not the proxy worker: the proxy rejects preferredOutputLocation
  // outright, and a worker-owned GPUDevice can't share gpu-buffers with the
  // main-thread renderer. The whole gpu-buffer path depends on this.
  ort.env.wasm.proxy = false;
}

export type DecoderKind = "geom" | "appr";
export type Precision = "fp16" | "fp32";

export interface CreateResolvedDecoderArgs {
  kind: DecoderKind; // decoderResolve's downgrade taxonomy
  label?: string; // 'head.geom' — logs only, not the taxonomy
  want16: boolean; // caller pre-gates on EP + ogsGpuFlags + the node's precision pick
  fp16Src?: string | Uint8Array;
  onWebgpu?: boolean;
  getFp32: () => Promise<string | Uint8Array>;
  ort: OrtEnvLike;
  providers: string[];
  spec: DecoderSessionOptions;
  extra?: Record<string, unknown>; // preferredOutputLocation + profiler options
  onDowngrade?: (kind: DecoderKind, reason: string) => void;
}

/** Build one decoder at the best precision it can actually run: fp16-internal
 *  when asked for and buildable, else fp32 (fetched lazily) with a REPORTED
 *  downgrade — never a silent fallback. */
export async function createResolvedDecoder(
  args: CreateResolvedDecoderArgs,
): Promise<{ session: DecoderSession; usedFp16: boolean }> {
  const { kind, want16, fp16Src, getFp32, ort, providers, spec, extra, onDowngrade } = args;
  const res = await resolveDecoderSession<DecoderSession>({
    kind,
    want16,
    fp16Src,
    onWebgpu: args.onWebgpu ?? providers[0] === "webgpu",
    getFp32,
    create: (src) =>
      withOrtGate(() => {
        ensureOgsOrtEnv(ort);
        return DecoderSession.createFrom(ort as never, src, providers, spec, extra ?? {});
      }),
    onDowngrade,
  });
  if (args.label) {
    console.info(`[ogs] decoder '${args.label}': ${res.usedFp16 ? "fp16" : "fp32"} (internal weights; fp32 IO)`);
  }
  return res;
}

/** Warm cache of built decoder sessions keyed by RESOLVED precision.
 *
 *  Sessions are built LAZILY, so a normal boot holds exactly ONE precision —
 *  the resolved default (fp16 where the bundle ships it and the EP allows it).
 *  The second only materialises if someone actually flips the Avatar node's
 *  precision dropdown, and from then on both stay resident so further toggles
 *  are an instant reference swap — no rebuild, no network, no reload. So the
 *  ~2× decoder VRAM is a cost the AUTHORING flow opts into by toggling, never
 *  something a shipped session pays. */
export class PrecisionCache {
  private built: Partial<Record<Precision, DecoderSession>> = {};

  /** Record an already-built session (the boot one) under its resolved precision. */
  seed(usedFp16: boolean, session: DecoderSession): void {
    this.built[usedFp16 ? "fp16" : "fp32"] = session;
  }

  has(p: Precision): boolean {
    return !!this.built[p];
  }

  /** The session for `want16`, building it once via `build` on a miss. Caches
   *  under the RESOLVED precision (an fp16 request can auto-downgrade), so a
   *  device that can't build fp16 doesn't retry the build on every toggle. */
  async get(
    want16: boolean,
    build: (want16: boolean) => Promise<{ session: DecoderSession; usedFp16: boolean }>,
  ): Promise<{ session: DecoderSession; usedFp16: boolean }> {
    const key: Precision = want16 ? "fp16" : "fp32";
    const hit = this.built[key];
    // usedFp16 is the RESOLVED precision, which is only 'fp16' when the fp16
    // session is the one under the fp16 key — a downgraded request is recorded
    // under BOTH keys (below) but is an fp32 session, so ask the session, never
    // the key. Getting this wrong makes reportPrecision lie.
    if (hit) return { session: hit, usedFp16: hit === this.built.fp32 ? false : key === "fp16" };
    const res = await build(want16);
    this.built[res.usedFp16 ? "fp16" : "fp32"] = res.session;
    // An fp16 request that AUTO-DOWNGRADED: alias the fallback under the
    // requested key too, otherwise every toggle back to fp16 misses the cache
    // and re-runs the whole failing fp16 build (a stall under the engine's
    // mutex, plus a repeated downgrade warning in the asset indicator).
    if (want16 && !res.usedFp16) this.built.fp16 = res.session;
    return res;
  }

  /** Release every session this cache built (pipeline teardown). */
  dispose(): void {
    // Deduped: a downgraded fp16 request aliases ONE session under both keys,
    // and ORT's release() rejects on a second call.
    for (const s of new Set(Object.values(this.built))) s?.dispose();
    this.built = {};
  }
}
